import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, ArrowLeft, Pencil } from 'lucide-react';
import EnhancedTable from '../../../components/common/EnhancedTable';
import ExpandableText from '../../../components/common/ExpandableText';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import { DevFinancial } from '../../../types/financial';
import AddFinancialModal from './components/AddFinancialModal';
import AddIncomeModal from './components/AddIncomeModal';
import EditFinancialModal from './components/EditFinancialModal';

export default function FinancialsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddExpenseModalOpen, setIsAddExpenseModalOpen] = useState(false);
  const [isAddIncomeModalOpen, setIsAddIncomeModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedFinancial, setSelectedFinancial] = useState<DevFinancial | null>(null);

  const { data: financials, isLoading } = useQuery<DevFinancial[]>({
    queryKey: ['admin', 'dev_financials'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dev_financials')
        .select('*')
        .order('fin_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data;
    }
  });

  const filteredFinancials = financials?.filter(financial => 
    financial.fin_comm?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    financial.fin_supplier?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    financial.fin_desc?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'dev_financials'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Financials</h1>
            <p className="mt-1 text-sm text-gray-500">
              Add, edit, and manage financial records
            </p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setIsAddIncomeModalOpen(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Income
            </button>
            <button 
              onClick={() => setIsAddExpenseModalOpen(true)}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Expense
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center flex-1">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search financials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <EnhancedTable
          columns={[
            { 
              id: 'actions', 
              header: 'Edit', 
              accessor: (row) => (
                <button 
                  onClick={() => {
                    setSelectedFinancial(row);
                    setIsEditModalOpen(true);
                  }}
                  className="text-primary hover:text-primary-dark"
                >
                  <Pencil className="h-5 w-5" />
                </button>
              ),
              width: 'narrow'
            },
            { 
              id: 'community', 
              header: 'Community', 
              accessor: (row) => row.fin_comm || '-',
              width: 'medium'
            },
            { 
              id: 'date', 
              header: 'Date', 
              accessor: (row) => row.fin_date ? formatDate(row.fin_date) : '-',
              width: 'medium'
            },
            { 
              id: 'income', 
              header: 'Income Amount', 
              accessor: (row) => row.fin_inc_amt ? `$${row.fin_inc_amt.toFixed(2)}` : '-',
              width: 'medium',
              align: 'right'
            },
            { 
              id: 'expense', 
              header: 'Expense Amount', 
              accessor: (row) => row.fin_exp_amt ? `$${row.fin_exp_amt.toFixed(2)}` : '-',
              width: 'medium',
              align: 'right'
            },
            { 
              id: 'description', 
              header: 'Description', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.fin_desc || 'Not specified'} 
                  maxLength={50} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            },
            { 
              id: 'project', 
              header: 'Project', 
              accessor: (row) => row.fin_exp_proj || '-',
              width: 'medium'
            },
            { 
              id: 'supplier', 
              header: 'Supplier', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.fin_supplier || 'Not specified'} 
                  maxLength={30} 
                  className="text-sm"
                />
              ),
              width: 'medium'
            }
          ]}
          data={filteredFinancials || []}
          keyField="fin_id"
          width="3000px"
          maxHeight="calc(100vh - 16rem)"
          loading={isLoading}
          emptyMessage="No financial records found"
          horizontalScroll={true}
        />
      </div>

      <AddFinancialModal
        isOpen={isAddExpenseModalOpen}
        onClose={() => setIsAddExpenseModalOpen(false)}
        onSuccess={handleSuccess}
      />

      <AddIncomeModal
        isOpen={isAddIncomeModalOpen}
        onClose={() => setIsAddIncomeModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedFinancial && (
        <EditFinancialModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedFinancial(null);
          }}
          onSuccess={handleSuccess}
          financial={selectedFinancial}
        />
      )}
    </div>
  );
}
